import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, RotateCcw, Undo2, HelpCircle, Trophy, Loader2, ArrowRight, X } from "lucide-react";
import {
  HONEY_COLORS,
  levelConfig,
  generateLevel,
  pour,
  isSolved,
} from "@/lib/honeysort";

/* ── Jar rendering geometry (SVG units) ───────────────────────────────────── */
const LAYER_H = 22;
const HONEY_W = 40;
const WALL = 8;
const LID_H = 13;
const NECK_H = 7;
const TOP_GAP = 6;
const BASE = 9;
const BODY_W = HONEY_W + WALL * 2; // 56
const SVG_W = BODY_W + 8; // 64
const JAR_X = (SVG_W - BODY_W) / 2; // 4
const HONEY_X = JAR_X + WALL; // 12

function JarSVG({ layers, capacity, selected, canReceive, isSuper, scale = 1, clipId }) {
  const honeyTop = LID_H + NECK_H + TOP_GAP;
  const honeyH = capacity * LAYER_H;
  const honeyBottom = honeyTop + honeyH;
  const bodyTop = LID_H + NECK_H;
  const bodyBottom = honeyBottom + BASE;
  const svgH = bodyBottom + 4;

  const rects = [];
  for (let i = 0; i < layers.length; i++) {
    const y = honeyBottom - (i + 1) * LAYER_H;
    const col = HONEY_COLORS[layers[i]] || { fill: "#ccc", edge: "#999" };
    rects.push(
      <g key={i}>
        <rect x={HONEY_X} y={y} width={HONEY_W} height={LAYER_H} fill={col.fill} />
        {/* separator between this layer and the one above */}
        <rect x={HONEY_X} y={y} width={HONEY_W} height={3} fill={col.edge} opacity="0.55" />
      </g>,
    );
  }

  return (
    <svg
      viewBox={`0 0 ${SVG_W} ${svgH}`}
      width={SVG_W * scale}
      height={svgH * scale}
      role="img"
      style={{ overflow: "visible", display: "block" }}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x={HONEY_X} y={honeyTop} width={HONEY_W} height={honeyH} rx="9" ry="9" />
        </clipPath>
        <linearGradient id={`${clipId}-lid`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E8B15A" />
          <stop offset="100%" stopColor="#B9821F" />
        </linearGradient>
      </defs>

      {/* glass body */}
      <rect
        x={JAR_X}
        y={bodyTop}
        width={BODY_W}
        height={bodyBottom - bodyTop}
        rx="13"
        ry="13"
        fill={canReceive ? "rgba(250,204,21,0.14)" : "rgba(255,255,255,0.45)"}
        stroke={selected ? "#F59E0B" : canReceive ? "#FBBF24" : "#D6D3D1"}
        strokeWidth={selected || canReceive ? 3 : 2}
      />

      {/* honey layers, clipped to the inner jar */}
      <g clipPath={`url(#${clipId})`}>{rects}</g>

      {/* glass shine + rim, drawn over the honey */}
      <rect x={JAR_X} y={bodyTop} width={BODY_W} height={bodyBottom - bodyTop} rx="13" ry="13" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1" opacity="0.7" />
      <rect x={JAR_X + 5} y={honeyTop} width="5" height={honeyH} rx="2" fill="rgba(255,255,255,0.35)" />

      {/* neck + lid */}
      <rect x={JAR_X + 6} y={LID_H - 2} width={BODY_W - 12} height={NECK_H + 4} rx="4" fill="rgba(255,255,255,0.5)" stroke="#D6D3D1" strokeWidth="1.5" />
      <rect x={JAR_X + 3} y="0" width={BODY_W - 6} height={LID_H} rx="4" fill={`url(#${clipId}-lid)`} stroke="#8A5E14" strokeWidth="1" />
      <rect x={JAR_X + 7} y="2.5" width={BODY_W - 14} height="3" rx="1.5" fill="rgba(255,255,255,0.45)" />

      {isSuper && (
        <text x={SVG_W / 2} y={honeyTop + honeyH / 2} textAnchor="middle" fontSize="9" fontWeight="700" fill="#B45309" opacity={layers.length ? 0 : 0.5}>
          BUFFER
        </text>
      )}
    </svg>
  );
}

export default function HoneySort() {
  const [loaded, setLoaded] = useState(false);
  const [level, setLevel] = useState(1);
  const [board, setBoard] = useState([]);
  const [moves, setMoves] = useState(0);
  const [wins, setWins] = useState(0);
  const [bestLevel, setBestLevel] = useState(1);

  const [selected, setSelected] = useState(null);
  const [invalid, setInvalid] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const historyRef = useRef([]); // [{board, moves}] for undo (not persisted)
  const wonHandledRef = useRef(false);

  const cfg = useMemo(() => levelConfig(level), [level]);
  const { capacities, superIndex } = cfg;

  const solved = useMemo(
    () => board.length > 0 && board.some((j) => j.length) && isSolved(board),
    [board],
  );

  /* ── load progress ──────────────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    api
      .get("/games/honeysort")
      .then((r) => {
        if (cancelled) return;
        const d = r.data || {};
        const lvl = d.level || 1;
        const c = levelConfig(lvl);
        const savedOk =
          Array.isArray(d.board) &&
          d.board.length === c.capacities.length &&
          d.board.every((jar, i) => Array.isArray(jar) && jar.length <= c.capacities[i]);
        setLevel(lvl);
        setBoard(savedOk ? d.board : generateLevel(lvl));
        setMoves(savedOk ? d.moves || 0 : 0);
        setWins(d.wins || 0);
        setBestLevel(d.best_level || lvl);
      })
      .catch(() => {
        setBoard(generateLevel(1));
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── persist (debounced) ────────────────────────────────────────────────── */
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!loaded) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api
        .put("/games/honeysort", {
          level,
          board,
          moves,
          wins,
          best_level: Math.max(bestLevel, level),
        })
        .catch(() => {});
    }, 500);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [loaded, level, board, moves, wins, bestLevel]);

  /* ── win handling ───────────────────────────────────────────────────────── */
  useEffect(() => {
    if (solved && !wonHandledRef.current) {
      wonHandledRef.current = true;
      setSelected(null);
      setWins((w) => w + 1);
      setBestLevel((b) => Math.max(b, level + 1));
      setShowWin(true);
    }
    if (!solved) wonHandledRef.current = false;
  }, [solved, level]);

  /* ── interactions ───────────────────────────────────────────────────────── */
  const flashInvalid = useCallback((i) => {
    setInvalid(i);
    setTimeout(() => setInvalid(null), 320);
  }, []);

  const onJarClick = useCallback(
    (i) => {
      if (solved) return;
      if (selected === null) {
        if (board[i].length > 0) setSelected(i);
        return;
      }
      if (selected === i) {
        setSelected(null);
        return;
      }
      const next = pour(board, capacities, selected, i);
      if (next) {
        historyRef.current.push({ board, moves });
        if (historyRef.current.length > 300) historyRef.current.shift();
        setBoard(next);
        setMoves((m) => m + 1);
        setSelected(null);
      } else if (board[i].length > 0) {
        setSelected(i); // reselect a new source
      } else {
        flashInvalid(i);
        setSelected(null);
      }
    },
    [solved, selected, board, capacities, moves, flashInvalid],
  );

  const undo = useCallback(() => {
    const prev = historyRef.current.pop();
    if (!prev) return;
    setBoard(prev.board);
    setMoves(prev.moves);
    setSelected(null);
  }, []);

  const reset = useCallback(() => {
    historyRef.current = [];
    setBoard(generateLevel(level));
    setMoves(0);
    setSelected(null);
    setShowWin(false);
    wonHandledRef.current = false;
  }, [level]);

  const nextLevel = useCallback(() => {
    const nl = level + 1;
    historyRef.current = [];
    setLevel(nl);
    setBoard(generateLevel(nl));
    setMoves(0);
    setSelected(null);
    setShowWin(false);
    wonHandledRef.current = false;
    setBestLevel((b) => Math.max(b, nl));
    toast.success(`Level ${nl}`);
  }, [level]);

  /* highlight jars that could legally receive from the selected jar */
  const receiveSet = useMemo(() => {
    if (selected === null) return new Set();
    const s = new Set();
    for (let t = 0; t < board.length; t++) {
      if (t !== selected && pour(board, capacities, selected, t)) s.add(t);
    }
    return s;
  }, [selected, board, capacities]);

  if (!loaded) {
    return (
      <div className="grid place-items-center py-24 text-stone-400">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const superLayers = board[superIndex] || [];
  const superScale = cfg.superCap > 6 ? 0.72 : 0.85;

  const renderJar = (i, { isSuper = false } = {}) => (
    <button
      key={i}
      data-testid={`jar-${i}`}
      onClick={() => onJarClick(i)}
      className="relative transition-transform outline-none"
      style={{
        transform: selected === i ? "translateY(-10px)" : "none",
        filter: selected === i ? "drop-shadow(0 8px 10px rgba(245,158,11,0.45))" : "none",
        animation: invalid === i ? "hs-shake 0.32s" : "none",
      }}
    >
      <JarSVG
        layers={board[i] || []}
        capacity={capacities[i]}
        selected={selected === i}
        canReceive={receiveSet.has(i)}
        isSuper={isSuper}
        scale={isSuper ? superScale : 1}
        clipId={`hs-clip-${i}`}
      />
    </button>
  );

  return (
    <div className="space-y-5" data-testid="honeysort-page">
      <style>{`@keyframes hs-shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-5px)}40%{transform:translateX(5px)}60%{transform:translateX(-4px)}80%{transform:translateX(4px)}}`}</style>

      {/* header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/app/games" className="text-stone-500 hover:text-amber-600" data-testid="honeysort-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2">
              🍯 Honey Sort
            </h1>
            <p className="text-sm text-stone-500">
              Level <span className="font-semibold text-stone-700">{level}</span> · {cfg.colors} colours ·{" "}
              buffer holds {cfg.superCap} · {moves} moves
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={undo} data-testid="honeysort-undo" disabled={historyRef.current.length === 0}>
            <Undo2 className="w-4 h-4 mr-1" /> Undo
          </Button>
          <Button variant="outline" size="sm" onClick={reset} data-testid="honeysort-reset">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)} data-testid="honeysort-help">
            <HelpCircle className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* super buffer (left) + regular jars grid (right) */}
      <div className="flex flex-wrap items-center justify-center gap-6 pt-2">
        <div className="flex shrink-0 flex-col items-center gap-1 rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-amber-700/80">Super Jar</span>
          {renderJar(superIndex, { isSuper: true })}
          <span className="text-xs text-amber-700/70">
            buffer {superLayers.length}/{cfg.superCap}
          </span>
        </div>

        <div
          className="grid justify-center gap-x-3 gap-y-6"
          style={{ gridTemplateColumns: "repeat(4, minmax(0, max-content))" }}
        >
          {Array.from({ length: superIndex }, (_, i) => renderJar(i))}
        </div>
      </div>

      {/* win overlay */}
      {showWin && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="honeysort-win">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-amber-100">
              <Trophy className="h-7 w-7 text-amber-500" />
            </div>
            <h2 className="font-display text-2xl font-semibold">Level {level} sorted! 🍯</h2>
            <p className="mt-1 text-stone-500">Every jar is a single colour. Solved in {moves} moves.</p>
            <div className="mt-5 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={reset}>
                Replay
              </Button>
              <Button className="flex-1" onClick={nextLevel} data-testid="honeysort-next">
                Next level <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* help modal */}
      {showHelp && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setShowHelp(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold">How to play</h2>
              <button onClick={() => setShowHelp(false)} className="text-stone-400 hover:text-stone-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="list-disc space-y-2 pl-5 text-sm text-stone-600">
              <li>Tap a jar to pick it up, then tap another to <b>pour</b> the top honey layers across.</li>
              <li>You can only pour onto an <b>empty jar</b> or onto a layer of the <b>same colour</b>.</li>
              <li>The whole run of matching top layers pours at once (as far as there's room).</li>
              <li>Use the tall <b>Super Jar</b> as a buffer to park layers while you sort.</li>
              <li>Finish the level when <b>every filled jar holds a single colour</b>.</li>
              <li><b>Undo</b> steps back, <b>Reset</b> restarts the same level. Progress saves automatically.</li>
            </ul>
            <Button className="mt-5 w-full" onClick={() => setShowHelp(false)}>
              Got it
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
